import { FunctionComponent, useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Staff from 'model/Staff';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { NewBookingAlert } from '@spike/new-booking-model';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import StaffComponent from './StaffComponent';
import { PetServiceWrapper } from 'model/NewMultipleBooking';
import { StaffStatus } from 'reducers/staff/StaffState';
import useNonInitialEffect from '@versiondos/hooks';
import { Spinner } from 'components/UI';
import { TrainingService, TransportationService, WalkingService } from 'model/Service';

interface Props {
    loading?: boolean;
    service?: PetServiceWrapper | TransportationService | TrainingService | WalkingService;
    type?: 'pet' | 'transportation' | 'training' | 'walking';
    selectedStaff?: number;
    alerts?: Array<NewBookingAlert>;
    // Walking passes false: check_alerts only evaluates session, zone and capacity when a
    // staff member is sent, so an auto-pick makes the drawer warn about a walker nobody chose.
    autoSelect?: boolean;
    onSelectStaff: (staff?: Staff) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            borderBottom: 'none',
            paddingBottom: 18,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 20
            }
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        alert: {
            marginBottom: '0px !important',
            marginTop: 18,
            [theme.breakpoints.up('md')]: {
                marginTop: 20
            }
        },
        alertText: {
            fontSize: 14,
            lineHeight: '20px'
        },
        withoutBorder: {
            borderBottom: 'none !important'
        }
    })
);

export const StaffSelector: FunctionComponent<Props> = props => {
    const classes = useStyles(props);

    const allActiveStaff = useSelector<RootState, Array<Staff>>(state => state.staff.staff).filter(
        member => member.active && !member.deleted
    );
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);

    const options = useMemo(() => {
        const serviceId =
            props.type === 'transportation'
                ? (props.service as TransportationService).serviceId!
                : props.type === 'walking'
                ? (props.service as WalkingService).serviceId!
                : props.type === 'training'
                ? (props.service as TrainingService).serviceId!
                : (props.service as PetServiceWrapper)?.service?.id;
        if (!serviceId) return [];
        return allActiveStaff.filter(member => member.services.some(s => s.id === serviceId));
    }, [allActiveStaff, props.service, props.type]);

    const [staffSelected, setStaffSelected] = useState<Staff | undefined>();
    const [alerts, setAlerts] = useState<Array<NewBookingAlert>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const userSelectionRef = useRef<boolean>(false);

    const ready = useMemo(() => {
        const serviceId =
            props.type === 'transportation'
                ? (props.service as TransportationService)?.serviceId
                : props.type === 'walking'
                ? (props.service as WalkingService)?.serviceId
                : props.type === 'training'
                ? (props.service as TrainingService)?.serviceId
                : (props.service as PetServiceWrapper)?.service?.id;
        return Boolean(serviceId && options.length > 0);
    }, [props.service, props.type, options.length]);

    useEffect(() => {
        if (!ready) {
            if (staffSelected) {
                setStaffSelected(undefined);
                props.onSelectStaff(undefined);
            }
            userSelectionRef.current = false;
            return;
        }

        if (userSelectionRef.current) {
            userSelectionRef.current = false;
            return;
        }

        const fromProp = options.find(s => s.id === props.selectedStaff);

        if (props.autoSelect === false) {
            if (!fromProp) {
                if (staffSelected) {
                    setStaffSelected(undefined);
                    props.onSelectStaff(undefined);
                }
                return;
            }
            if (staffSelected?.id !== fromProp.id) {
                setStaffSelected(fromProp);
                props.onSelectStaff(fromProp);
            }
            return;
        }

        const next = fromProp ?? options[0];

        if (!staffSelected || (fromProp && staffSelected.id !== fromProp.id)) {
            setStaffSelected(next);
            props.onSelectStaff(next);
        } else if (!fromProp && next && (!staffSelected || staffSelected.id !== next.id)) {
            setStaffSelected(next);
            props.onSelectStaff(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, props.selectedStaff, props.autoSelect, options]);

    useEffect(() => {
        if (props.alerts) {
            const alertBusy = props.alerts.find(alert => alert.message === 'Staff busy');
            if (alertBusy) {
                setAlerts([alertBusy]);
            } else {
                setAlerts(props.alerts);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.alerts]);

    useNonInitialEffect(() => {
        switch (staffStatus) {
            case StaffStatus.Fetching:
                setLoading(true);
                break;
            case StaffStatus.FetchSuccess:
                setLoading(false);
                break;
            case StaffStatus.Error:
                setLoading(false);
        }
    }, [staffStatus]);

    const getAlertMessage = (message: string) => {
        if (message === 'Staff not working on that date/time') {
            return `Conflicts with ${staffSelected?.person.firstName} ${staffSelected?.person.lastName}’s working hours.`;
        } else if (message === 'Staff busy') {
            return `${staffSelected?.person.firstName} ${staffSelected?.person.lastName} is already book at this time, but you can still double-book appointments for them.`;
        } else {
            return message;
        }
    };

    return (
        <Box className={classes.container}>
            {props.loading || loading ? (
                <Spinner />
            ) : (
                <StaffComponent
                    selectedOption={staffSelected}
                    options={options}
                    onSelectStaff={(staff: Staff) => {
                        userSelectionRef.current = true;
                        setStaffSelected(staff);
                        props.onSelectStaff(staff);
                    }}
                />
            )}
            {alerts?.map(alert => (
                <Box
                    key={`booking_div_${alert.alertType}_alert`}
                    id={`booking_div_${alert.alertType}_alert`}
                >
                    <AlertNewBooking
                        className={classes.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EAB464"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <Typography className={classes.alertText}>{getAlertMessage(alert.message)}</Typography>
                    </AlertNewBooking>
                </Box>
            ))}
        </Box>
    );
};

export default StaffSelector;
